// Pure projection of the native Data read onto the Data tab's own view model. A value that is absent
// (null or missing) is null and draws as a dash, never zero or empty text; nothing is invented: no count
// beyond the rows actually returned, no change time (the read's `fetchedAt` is when it was READ, not
// when a row changed), no history.
import type { NativeCell, NativeCollection, NativeColumn, NativeDataset } from '../native-data-api';
import type { DataCell, DataColumn, DataRow, DataTable, RowsState } from './data-model';

const numeric = (column: NativeColumn) => column.type === 'integer' || column.type === 'decimal';

export function projectColumns(columns: readonly NativeColumn[]): DataColumn[] {
  return columns.map((column, index) => ({
    key: column.id,
    label: column.label,
    type: column.type,
    ...(numeric(column) ? { numeric: true } : {}),
    ...(index === 0 ? { lead: true } : {}),
  }));
}

export function projectCell(cell: NativeCell | undefined, column: NativeColumn): DataCell {
  if (!cell || cell.status !== 'present') return null;
  const { type, value } = cell.value;
  if (type !== column.type) return null; // a value that is not its column's type is not shown
  switch (type) {
    case 'text':
    case 'date':
    case 'timestamp':
    case 'integer':
      return typeof value === 'string' ? value : null;
    case 'decimal':
      return typeof value === 'string'
        ? column.currency
          ? `${value} ${column.currency}`
          : value
        : null;
    case 'boolean':
      return typeof value === 'boolean' ? value : null;
    case 'json':
      return JSON.stringify(value) ?? null;
    default:
      return null; // files are not drawn here
  }
}

export function projectRows(dataset: NativeDataset): DataRow[] {
  return dataset.rows.map((row) => ({
    id: row.id,
    cells: Object.fromEntries(
      dataset.columns.map((column) => [column.id, projectCell(row.cells[column.id], column)]),
    ),
  }));
}

export function projectRowsState(dataset: NativeDataset, canShowMore: boolean): RowsState {
  return {
    status: 'ready',
    columns: projectColumns(dataset.columns),
    rows: projectRows(dataset),
    // "more" is offered only when the server says there is more AND the tab reads another page
    hasMore: dataset.hasMore && canShowMore,
  };
}

/**
 * A collection that has no name of its own for a person ("snapshots", "watch_snapshots") reads as words until the App
 * declares one: separators become spaces and the first letter is capitalised. A name that already has spaces was written
 * for a person and is kept exactly.
 */
export function humanizeTableName(name: string): string {
  if (/\s/u.test(name)) return name;
  const words = name.replace(/[_-]+/gu, ' ').trim();
  return words ? words.charAt(0).toLocaleUpperCase() + words.slice(1) : name;
}

/** The entry count is known only for a complete read: the rows returned, nothing estimated. */
export function projectTable(
  collection: NativeCollection,
  dataset: NativeDataset | null,
): DataTable {
  return {
    id: collection.id,
    name: humanizeTableName(collection.name),
    kind: 'table',
    fields: collection.columns.length,
    ...(dataset && !dataset.hasMore ? { entries: dataset.returnedRows } : {}),
  };
}
